"use client"
import {
  Box,
  Image as ChakraImage,
  HStack,
  Icon,
  SegmentGroup,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react"
import parse from "html-react-parser"
import { type ComponentType, type FC, useMemo, useState } from "react"

import BlurFade from "@/components/Animations/BlurFade"
import ClientZoom from "@/components/ClientZoom"
import { MobileDeviceIcon, MonitorIcon, TabletIcon } from "@/components/icons"
import Img from "@/components/Img"
import MarkdownParser from "@/components/MarkdownParser"
import { Tooltip } from "@/components/ui/tooltip"
import { type MockupBlock as MockupBlockType } from "@/types/database"
import { getSafeHtmlParserOptions } from "@/utils/getSafeHtmlParserOptions"
import getStickyContentBlockLeftProps from "@/utils/getStickyContentBlockLeftProps"

import ContentBlockTitle, {
  type ContentBlockTitleNumberProps,
} from "./ContentBlockTitle"

type DeviceKey = "desktop" | "tablet" | "mobile"

type MockupMedia = NonNullable<MockupBlockType["desktop_image"]>

interface DeviceConfig {
  dimensions: string
  icon: ComponentType
  label: string
  maxWidth: string
  rounded: string
  value: DeviceKey
  viewportWidth: string
}

const DEVICE_CONFIGS: Record<DeviceKey, DeviceConfig> = {
  desktop: {
    dimensions: "1440 x 900",
    icon: MonitorIcon,
    label: "Desktop",
    maxWidth: "100%",
    rounded: "0",
    value: "desktop",
    viewportWidth: "100%",
  },
  mobile: {
    dimensions: "390 x 844",
    icon: MobileDeviceIcon,
    label: "Mobile",
    maxWidth: "calc(100% - 32px)",
    rounded: "24px",
    value: "mobile",
    viewportWidth: "390px",
  },
  tablet: {
    dimensions: "768 x 1024",
    icon: TabletIcon,
    label: "Tablet",
    maxWidth: "calc(100% - 48px)",
    rounded: "10px",
    value: "tablet",
    viewportWidth: "768px",
  },
}

const DEVICE_BUTTON_ORDER: DeviceKey[] = ["desktop", "tablet", "mobile"]
const DEFAULT_DEVICE_ORDER: DeviceKey[] = ["desktop", "tablet", "mobile"]

function getDirectusAssetUrl(fileId: string) {
  const baseUrl = process.env.DIRECTUS_ASSETS_URL ?? ""
  return `${baseUrl.replace(/\/+$/, "")}/${encodeURIComponent(fileId)}`
}

function hasMedia(media: MockupBlockType["desktop_image"]) {
  if (!media) {
    return false
  }

  if (typeof media === "string") {
    return media.trim().length > 0
  }

  return Boolean(media.id)
}

function isDirectusSvg(media: MockupMedia) {
  if (typeof media === "string") {
    return /\.svg(?:[?#].*)?$/i.test(media.trim())
  }

  return (
    media.type === "image/svg+xml" ||
    media.filename_download?.toLowerCase().endsWith(".svg") ||
    media.filename_disk?.toLowerCase().endsWith(".svg")
  )
}

function isRawSvg(media: MockupMedia): media is string {
  return typeof media === "string" && media.trim().startsWith("<svg")
}

function getMediaTitle(media: MockupMedia, fallback: string) {
  if (typeof media === "string") {
    return fallback
  }

  return media.title || media.description || fallback
}

function getMediaDescription(media: MockupBlockType["desktop"]) {
  if (!media || typeof media === "string") {
    return null
  }

  return media.description?.trim() || null
}

function getZoomImage(media: MockupMedia, alt: string) {
  if (isRawSvg(media)) {
    return undefined
  }

  if (typeof media === "string") {
    return { alt, src: media }
  }

  return media.id ? { alt, src: getDirectusAssetUrl(media.id) } : undefined
}

interface MockupImageProps {
  alt: string
  media: MockupMedia
}

const MockupImage: FC<MockupImageProps> = ({ alt, media }) => {
  if (isRawSvg(media)) {
    return (
      <Box
        aria-label={alt}
        css={{
          "& svg": {
            display: "block",
            height: "auto",
            maxWidth: "100%",
            width: "100%",
          },
        }}
        role="img"
        w="full"
      >
        {parse(media, getSafeHtmlParserOptions())}
      </Box>
    )
  }

  if (typeof media === "string") {
    return (
      <ChakraImage
        alt={alt}
        display="block"
        fit="contain"
        h="auto"
        maxW="100%"
        src={media}
        w="full"
      />
    )
  }

  if (isDirectusSvg(media)) {
    return (
      <ChakraImage
        alt={alt}
        display="block"
        fit="contain"
        h="auto"
        maxW="100%"
        src={getDirectusAssetUrl(media.id)}
        w="full"
      />
    )
  }

  return (
    <Img
      alt={alt}
      breakpointWidths={{
        base: "360",
        sm: "420",
        md: "768",
        lg: "1024",
        xl: "1376",
      }}
      h="auto"
      height="auto"
      noContainer
      objectFit="contain"
      originalHeight={media.height ?? undefined}
      originalWidth={media.width ?? undefined}
      src={media.id}
      transform={{
        fit: "contain",
        format: "webp",
        quality: 95,
      }}
      w="full"
      width="100%"
    />
  )
}

interface MockupBlockProps extends ContentBlockTitleNumberProps {
  block: MockupBlockType
}

const MockupBlock: FC<MockupBlockProps> = ({ block, titleNumber }) => {
  const {
    caption,
    darker_bg,
    description,
    mockup_bar_title,
    show_image_description,
    title,
    two_columns_mockup,
    url,
  } = block

  const mediaByDevice = useMemo(
    () => ({
      desktop: block.desktop_image ?? block.desktop ?? block.desktop_svg,
      mobile: block.mobile_image ?? block.mobile ?? block.mobile_svg,
      tablet: block.tablet_image ?? block.tablet ?? block.tablet_svg,
    }),
    [
      block.desktop,
      block.desktop_image,
      block.desktop_svg,
      block.mobile,
      block.mobile_image,
      block.mobile_svg,
      block.tablet,
      block.tablet_image,
      block.tablet_svg,
    ]
  )

  const availableDevices = useMemo(
    () =>
      DEVICE_BUTTON_ORDER.filter((device) => hasMedia(mediaByDevice[device])),
    [mediaByDevice]
  )

  const defaultDevice = useMemo(
    () =>
      DEFAULT_DEVICE_ORDER.find((device) => hasMedia(mediaByDevice[device])) ??
      "desktop",
    [mediaByDevice]
  )

  const [selectedDevice, setSelectedDevice] = useState<DeviceKey>(defaultDevice)

  const activeDevice = hasMedia(mediaByDevice[selectedDevice])
    ? selectedDevice
    : defaultDevice
  const activeMedia = mediaByDevice[activeDevice]
  const activeConfig = DEVICE_CONFIGS[activeDevice]
  const trimmedTitle = title?.trim()
  const trimmedDescription = description?.trim()
  const hasTitle = Boolean(trimmedTitle)
  const hasDescription = Boolean(trimmedDescription)
  const fallbackAlt = `${activeConfig.label} mockup preview`
  const imageAlt = activeMedia
    ? getMediaTitle(activeMedia, trimmedTitle || fallbackAlt)
    : fallbackAlt
  const zoomImg = activeMedia ? getZoomImage(activeMedia, imageAlt) : undefined
  const activeImageDescription = show_image_description
    ? getMediaDescription(activeMedia)
    : null
  const hasFrameCaption = Boolean(caption || activeImageDescription)
  const frameBarTitle = mockup_bar_title?.trim() || url || "preview"

  return (
    <Stack
      alignItems="flex-start"
      bgColor={darker_bg ? "bg.subtle" : "transparent"}
      className="mockup-block internal-padding"
      direction={{
        base: "column",
        lg: two_columns_mockup ? "row" : "column",
      }}
      gap={{ base: 8, lg: two_columns_mockup ? 6 : 8 }}
      justifyContent="flex-end"
    >
      {(hasTitle || hasDescription) && (
        <VStack
          alignItems="flex-start"
          flexGrow={1}
          maxWidth={{
            base: "100%",
            lg: two_columns_mockup ? "30%" : "100%",
          }}
          {...getStickyContentBlockLeftProps(Boolean(two_columns_mockup))}
        >
          <BlurFade flexDirection="column" gap={0} inView>
            <ContentBlockTitle title={trimmedTitle} titleNumber={titleNumber} />
            {hasDescription && (
              <MarkdownParser
                color="fg.muted"
                fontSize="sm"
                markdown={trimmedDescription}
                pProps={{ fontFamily: "mono" }}
              />
            )}
          </BlurFade>
        </VStack>
      )}

      <VStack
        alignItems="stretch"
        flexBasis={{ base: "auto", lg: "0" }}
        flexGrow={1}
        gap={4}
        maxWidth={{
          base: "100%",
          lg: two_columns_mockup ? "70%" : "100%",
        }}
        minW={0}
        pl={!hasTitle && two_columns_mockup ? { base: 0, md: 12 } : 0}
        py={0}
        w="full"
      >
        <BlurFade flexDirection="column" gap={4} inView>
          <Box
            as="figure"
            bg="bg"
            border="1px solid"
            borderColor="border.muted"
            // boxShadow="0 1px 1px rgba(22, 21, 19, 0.04), 0 4px 8px rgba(22, 21, 19, 0.04), 0 16px 32px rgba(22, 21, 19, 0.06), 0 40px 72px rgba(22, 21, 19, 0.08)"
            boxShadow="xl"
            overflow="hidden"
            rounded="xl"
            w="full"
          >
            <HStack
              bg="bg.subtle"
              borderBottom="1px solid"
              borderColor="border.muted"
              gap={{ base: 2, md: 4 }}
              minH="12"
              px={{ base: 3, md: 4 }}
              py={3}
            >
              <HStack flexShrink={0} gap="1.5">
                <Box bg="#E8755C" boxSize="2.5" rounded="full" />
                <Box bg="#E9C063" boxSize="2.5" rounded="full" />
                <Box bg="#66BA68" boxSize="2.5" rounded="full" />
              </HStack>

              <Box
                alignItems="center"
                bg="bg"
                border="1px solid"
                borderColor="border.muted"
                color="fg.muted"
                display={{ base: "none", sm: "flex" }}
                flex="1"
                fontFamily="mono"
                fontSize="xs"
                gap={2}
                justifyContent="center"
                minW={0}
                px={3}
                py={1}
                rounded="md"
              >
                <Box bg="green.300" boxSize="2" flexShrink={0} rounded="full" />
                <Text
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {frameBarTitle}
                </Text>
                <Text as="span" color="fg.subtle" flexShrink={0}>
                  {activeConfig.label}
                </Text>
              </Box>

              {availableDevices.length > 1 && (
                <SegmentGroup.Root
                  aria-label="Preview device"
                  bg="bg.muted"
                  css={{
                    "--segment-indicator-bg": "var(--gil-colors-primary-500)",
                    "--segment-indicator-shadow": "var(--gil-shadows-md)",
                  }}
                  flexShrink={0}
                  height="auto"
                  onValueChange={({ value }) => {
                    if (
                      value === "desktop" ||
                      value === "tablet" ||
                      value === "mobile"
                    ) {
                      setSelectedDevice(value)
                    }
                  }}
                  rounded="md"
                  size="sm"
                  value={activeDevice}
                >
                  <SegmentGroup.Indicator
                    bg="primary.500"
                    border="1px solid"
                    borderColor="border.muted"
                    boxShadow="sm"
                    rounded="sm"
                  />
                  {availableDevices.map((device) => {
                    const config = DEVICE_CONFIGS[device]
                    const isSelected = device === activeDevice

                    return (
                      <Tooltip
                        content={`${config.label} preview`}
                        key={device}
                        positioning={{ placement: "top" }}
                      >
                        <SegmentGroup.Item
                          _hover={{
                            color: "fg",
                          }}
                          aria-label={`${config.label} preview`}
                          bg={isSelected ? "bg" : "transparent"}
                          border="1px solid"
                          borderColor={
                            isSelected ? "border.muted" : "transparent"
                          }
                          boxShadow={isSelected ? "sm" : "none"}
                          color={isSelected ? "fg" : "fg.muted"}
                          cursor="pointer"
                          h="7"
                          justifyContent="center"
                          minW="8"
                          px="2"
                          rounded="sm"
                          transition="background 160ms ease, border-color 160ms ease, box-shadow 160ms ease, color 160ms ease"
                          value={device}
                        >
                          <SegmentGroup.ItemText>
                            <Icon as={config.icon} size={"sm"} />
                          </SegmentGroup.ItemText>
                          <SegmentGroup.ItemHiddenInput />
                        </SegmentGroup.Item>
                      </Tooltip>
                    )
                  })}
                </SegmentGroup.Root>
              )}
            </HStack>

            <Box
              backgroundImage="linear-gradient(var(--gil-colors-bg) 1px, transparent 1px), linear-gradient(90deg, var(--gil-colors-bg) 1px, transparent 1px)"
              backgroundPosition="-1px -1px"
              backgroundSize="24px 24px"
              bg="bg.subtle"
              display="flex"
              justifyContent="center"
              overflow="hidden"
              py={activeDevice === "desktop" ? 0 : { base: 4, md: 8 }}
              transition="padding 320ms cubic-bezier(0.4, 0, 0.2, 1)"
              w="full"
            >
              <Box
                bg="bg"
                boxShadow={
                  activeDevice === "desktop"
                    ? "none"
                    : "0 2px 6px rgba(22, 21, 19, 0.06), 0 12px 24px rgba(22, 21, 19, 0.08)"
                }
                maxH={{ base: "540px", lg: "720px" }}
                maxW={activeConfig.maxWidth}
                overflow="auto"
                rounded={activeConfig.rounded}
                transition="width 320ms cubic-bezier(0.4, 0, 0.2, 1), max-width 320ms cubic-bezier(0.4, 0, 0.2, 1), border-radius 320ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 320ms cubic-bezier(0.4, 0, 0.2, 1)"
                w={activeConfig.viewportWidth}
              >
                {activeMedia ? (
                  <ClientZoom zoomImg={zoomImg}>
                    <MockupImage alt={imageAlt} media={activeMedia} />
                  </ClientZoom>
                ) : (
                  <Box
                    alignItems="center"
                    color="fg.muted"
                    display="flex"
                    fontSize="sm"
                    justifyContent="center"
                    minH="64"
                    px={6}
                    textAlign="center"
                  >
                    No mockup preview image available.
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {hasFrameCaption && (
            <HStack
              alignItems="baseline"
              as="figcaption"
              color="fg.muted"
              fontFamily="mono"
              fontSize="xs"
              gap={4}
              justifyContent="space-between"
              letterSpacing="wide"
              w="full"
            >
              <VStack alignItems="flex-start" flex="1" gap={1} minW={0}>
                {caption && <Text textTransform="uppercase">{caption}</Text>}
                {activeImageDescription && (
                  <Text
                    color="fg.subtle"
                    fontFamily="body"
                    fontSize="sm"
                    letterSpacing="normal"
                    textTransform="none"
                  >
                    {activeImageDescription}
                  </Text>
                )}
              </VStack>
              <Text as="span" color="fg.subtle">
                {activeConfig.label}
              </Text>
            </HStack>
          )}
        </BlurFade>
      </VStack>
    </Stack>
  )
}

export default MockupBlock
